/**
 * 三态规范的可复用实现：loading / empty / error。
 *
 * 规范要点（docs/frontend/ux-patterns.md）：
 *   - loading：表格用**骨架行**（保持列宽与行高，不产生布局跳动），200ms 内不显示任何 loading 提示；
 *   - empty：必须区分「数据集为空」与「筛选后为空」，因为下一步动作完全不同（建数据 vs 清筛选）；
 *   - error：必须给错误码 + 重试动作，并且**不吞错**（异常文本要能看到）。
 */

export interface StateViewProps {
  readonly kind: "loading" | "empty" | "error";
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string | null;
  readonly onAction: (() => void) | null;
}

export function StateView({ kind, title, description, actionLabel, onAction }: StateViewProps) {
  const tone =
    kind === "error"
      ? "border-danger/40 bg-danger-subtle"
      : kind === "empty"
        ? "border-line bg-surface"
        : "border-line-subtle bg-sunken";

  return (
    <div className={`m-4 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center ${tone}`}>
      {kind === "loading" ? (
        <div
          className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-accent"
          aria-hidden="true"
        />
      ) : null}
      <p
        className={`text-sm font-medium ${kind === "error" ? "text-danger-text" : "text-ink"}`}
        aria-live={kind === "error" ? "assertive" : "polite"}
      >
        {title}
      </p>
      <p className="max-w-md text-meta text-ink-2">{description}</p>
      {actionLabel === null || onAction === null ? null : (
        <button
          type="button"
          onClick={onAction}
          className="mt-1 rounded-control bg-accent px-3 text-table font-medium text-accent-ink transition-colors dur-fast hover:bg-accent-hover"
          style={{ height: "var(--control-height)" }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** 表格骨架行：与真实行**同高同列宽**，因此加载完成时不会产生 CLS。 */
export function TableSkeleton({
  rows = 12,
  gridTemplate,
  width,
}: {
  readonly rows?: number;
  readonly gridTemplate: string;
  readonly width: number;
}) {
  return (
    <div style={{ width }} aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_unused, index) => (
        <div
          key={index}
          className="grid items-center gap-0 border-b border-line-subtle px-cell-x"
          style={{ gridTemplateColumns: gridTemplate, height: "var(--row-height)" }}
        >
          {Array.from({ length: gridTemplate.split(" ").length }, (_cell, cellIndex) => (
            <div
              key={cellIndex}
              className="skeleton mx-1 h-3"
              style={{ width: cellIndex === 0 ? 12 : `${45 + ((index + cellIndex) % 4) * 12}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
